import SendIcon from "@mui/icons-material/Send"
import { Stack } from "@mui/material"
import { ReactElement, useContext } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"

import { useScreenSize } from "../../../hooks/useScreenSize"
import { INSTALLER_DRAFTS } from "../../../router/routes"
import useSubmitApplication from "../../../services/middleware/hooks/singleApplication/useSubmitApplication"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import AppButton from "../../buttons/appButton"
import AppLoadingButton from "../../buttons/appLoadingButton"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import { ApplicationContext } from "../../submitApplication/single/applicationProvider"
import MpanRecognisedInput from "../../submitApplication/single/recognisedInputs/mpanRecognisedInput"

const MPAN_REG = "mpan"

interface IProvideMpanManuallyFormProps {
    marginX: string
    submitApplicationManualMpan: (manuallyProvideMpan: string) => void
}

const ProvideMpanManuallyForm = ({
    marginX,
    submitApplicationManualMpan,
}: IProvideMpanManuallyFormProps): ReactElement => {
    const { control, handleSubmit, watch } = useForm({
        shouldUnregister: true,
        mode: "onChange",
    })

    const screenSize = useScreenSize()
    const { isMob } = screenSize

    const { setIsSubmitting, isSubmitting } = useSubmitApplication()
    const navigate = useNavigate()

    const { setCurrentStepIndex, onClickBackFirstStep } =
        useContext(ApplicationContext)

    const disableFormAndSubmitApplicationManualMpan = (): void => {
        setIsSubmitting(true)
        submitApplicationManualMpan(watch(MPAN_REG))
    }

    const submitCheckAddressDetails = (): void => {
        setCurrentStepIndex(0) // go to the "Basic Details" step in the journey
    }

    const openMyDrafts = (): void => {
        navigate(INSTALLER_DRAFTS)
    }

    return (
        <>
            <Stack
                marginX={marginX}
                marginY={isMob ? GRID_SPACING : GRID_SPACING * 2}
                spacing={isMob ? GRID_SPACING : GRID_SPACING * 2}
            >
                <MpanRecognisedInput
                    registerName={MPAN_REG}
                    control={control}
                    // We hardcode a GB postcode here to ensure we show GB instructions on the Mpan Input
                    // This assumption is valid as the form will only be shown in GB scenarios
                    postCode="CV1 2BA"
                    isMpanEntered={!!watch(MPAN_REG)}
                    isMeterNumberEntered={false}
                    forceShowMpanInput
                />
            </Stack>
            <Stack
                alignItems="center"
                textAlign="center"
                marginX={marginX}
                marginTop={isMob ? GRID_SPACING : GRID_SPACING * 2}
                spacing={isMob ? GRID_SPACING : GRID_SPACING * 1.5}
            >
                <AppLoadingButton
                    color="success"
                    variant="contained"
                    size="large"
                    onClick={handleSubmit(
                        disableFormAndSubmitApplicationManualMpan
                    )}
                    sx={{
                        width: { xs: "100%", md: "70%" },
                    }}
                    endIcon={<SendIcon />}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    type="submit"
                >
                    Submit
                </AppLoadingButton>
                <AppButton
                    variant="outlined"
                    size="large"
                    sx={{
                        width: { xs: "100%", md: "70%" },
                    }}
                    onClick={submitCheckAddressDetails}
                    disabled={isSubmitting}
                    type="button"
                >
                    Check Address Details
                </AppButton>
                <AppButton
                    variant="outlined"
                    size="large"
                    sx={{
                        width: { xs: "100%", md: "70%" },
                    }}
                    onClick={openMyDrafts}
                    disabled={isSubmitting}
                    type="button"
                >
                    My Drafts
                </AppButton>
                <AppAlert
                    severity="info"
                    sx={{
                        width: { xs: "100%", md: "70%" },
                    }}
                >
                    This application has been saved and is available within your
                    drafts. You can return to this application at any time to
                    provide the MPAN and submit the application.
                </AppAlert>
            </Stack>
        </>
    )
}

export default ProvideMpanManuallyForm
